import React from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { inventoryItemSchema, type InventoryItemFormData } from '../../types/inventory';
import { useSettingsStore } from '../../store/settingsStore';
import { formatRupiah, parseRupiah } from '../../utils/format';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { UnitToggle } from '../../components/common/UnitToggle';
import { WeightInput } from '../../components/common/WeightInput';
import { Modal } from '../../components/common/Modal';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';
import { runExclusive } from '../../utils/runExclusive';

interface CreateItemFormProps {
  /** Awaited: the submit button stays disabled until the returned promise settles. */
  onSubmit: (data: InventoryItemFormData) => void | Promise<void>;
  onCancel: () => void;
  initialData?: Partial<InventoryItemFormData>;
  mode?: 'create' | 'update';
  readOnly?: boolean;
}

const normalizeExpiryDate = (value?: string) => {
  if (!value) {
    return undefined;
  }
  return value.includes('T') ? value.slice(0, 10) : value;
};

export const CreateItemForm: React.FC<CreateItemFormProps> = ({ onSubmit, onCancel, initialData, mode, readOnly = false }) => {
  const { t } = useLanguage();
  const { currencySymbol, defaultUnit } = useSettingsStore();
  const { unit, toggleUnit } = useWeightUnit(defaultUnit as 'kg' | 'gram');
  const isUpdateMode = mode === 'update';
  const isFromWorkOrder = !!initialData?.workOrderId;
  const roastInputWeight = initialData?.roastInputWeight;
  const [isShrinkageWarningOpen, setIsShrinkageWarningOpen] = React.useState(false);
  const [pendingSubmitData, setPendingSubmitData] = React.useState<InventoryItemFormData | null>(null);
  const [currentShrinkage, setCurrentShrinkage] = React.useState<number>(0);
  // react-hook-form's isSubmitting only covers handleFormSubmit itself, which
  // used to hand off to the parent without awaiting it. The button came back
  // while the save was still running, and a second click posted the roasted
  // bulk twice — the second POST hit the one-item-per-work-order constraint
  // and showed "Failed to save finished good" next to the success toast.
  const savingLock = React.useRef(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const normalizedInitialData = React.useMemo(
    () => ({
      ...initialData,
      expiryDate: normalizeExpiryDate(initialData?.expiryDate),
    }),
    [initialData]
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<InventoryItemFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(inventoryItemSchema) as any,
    defaultValues: {
      reorderPoint: 0,
      reorderQuantity: 0,
      standardCost: 0,
      itemType: 'green_bean',
      quantity: 0,
      unitOfMeasure: defaultUnit,
      ...normalizedInitialData,
    },
  });

  const isBusy = isSubmitting || isSaving;

  const standardCost = useWatch({ control, name: 'standardCost' });

  React.useEffect(() => {
    if (initialData) {
      reset({
        reorderPoint: 0,
        reorderQuantity: 0,
        standardCost: 0,
        itemType: 'green_bean',
        quantity: 0,
        ...normalizedInitialData,
      });
    }
  }, [initialData, normalizedInitialData, reset]);

  const watchedQuantity = useWatch({ control, name: 'quantity' });
  const isOutputInvalid = Boolean(
    !watchedQuantity ||
      watchedQuantity <= 0 ||
      (isFromWorkOrder &&
        typeof roastInputWeight === 'number' &&
        roastInputWeight > 0 &&
        watchedQuantity > roastInputWeight)
  );

  React.useEffect(() => {
    if (!isFromWorkOrder) {
      return;
    }
    const totalInputCost = initialData?.roastTotalInputCost;
    if (!totalInputCost || totalInputCost <= 0) {
      return;
    }
    setValue('standardCost', totalInputCost, { shouldValidate: true, shouldDirty: true });
  }, [isFromWorkOrder, initialData?.roastTotalInputCost, setValue]);

  const submitToParent = (data: InventoryItemFormData) =>
    runExclusive(savingLock, async () => {
      setIsSaving(true);
      try {
        await onSubmit(data);
      } finally {
        setIsSaving(false);
      }
    });

  const handleFormSubmit = async (data: InventoryItemFormData) => {
    if (isFromWorkOrder && typeof roastInputWeight === 'number' && roastInputWeight > 0 && data.quantity > 0) {
      const shrinkage = ((roastInputWeight - data.quantity) / roastInputWeight) * 100;
      if (shrinkage > 25) {
        setPendingSubmitData(data);
        setCurrentShrinkage(shrinkage); // Simpan nilai shrinkage
        setIsShrinkageWarningOpen(true);
        return;
      }
    }
    await submitToParent(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.code')}</label>
            <input
              data-test-id="inventory-create-code"
              type="text"
              {...register('code')}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 bg-gray-100 cursor-not-allowed"
              placeholder={t('inventory.basicInfo.generatedBySystem')}
            />
            {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.customer.name')}</label>
            <input
              data-test-id="inventory-create-name"
              type="text"
              {...register('name', {
                maxLength: {
                  value: 100,
                  message: t('inventory.createItem.errors.max100Chars')
                }
              })}
              maxLength={100}
              disabled={readOnly}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.description')}</label>
            <textarea
              data-test-id="inventory-create-description"
              {...register('description')}
              rows={3}
              disabled={readOnly}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>

          {isFromWorkOrder && typeof roastInputWeight === 'number' && roastInputWeight > 0 && (
            <div className="sm:col-span-2">
              <div className="flex justify-between items-center bg-blue-100 border border-gray-800 rounded-md px-3 py-2 text-sm">
                <span className="text-gray-600">{t('inventory.createItem.roastInputFromWorkOrder')}</span>
                <span className="text-gray-900">{roastInputWeight.toFixed(3)} kg</span>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center h-6">
              <label className="block text-sm font-medium text-gray-700">
                {t('inventory.createItem.cogsBatch', { currencySymbol })}
              </label>
            </div>
            <input
              type="text"
              data-test-id="inventory-create-cost"
              value={formatRupiah(standardCost)}
              readOnly={isFromWorkOrder}
              onChange={(e) => {
                if (isFromWorkOrder) return;
                const raw = parseRupiah(e.target.value);
                setValue('standardCost', raw, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm 
              focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 
              ${isFromWorkOrder ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            {errors.standardCost && (
              <p className="mt-1 text-xs text-red-600">
                {errors.standardCost.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex justify-between items-center h-6">
              <label className="block text-sm font-medium text-gray-700">
                {t('inventory.createItem.roastOutput')} {initialData && <span className="text-xs text-gray-500">{t('inventory.createItem.updatesHistory')}</span>}
              </label>
              <UnitToggle unit={unit} onToggle={toggleUnit} disabled={isUpdateMode || readOnly} />
            </div>
            <div className="mt-1">
              <Controller
                control={control}
                name="quantity"
                rules={{
                  validate: (value) => {
                    if (!value || value <= 0) {
                      return t('inventory.createItem.errors.outputGreaterThanZero');
                    }
                    if (
                      isFromWorkOrder &&
                      typeof roastInputWeight === 'number' &&
                      roastInputWeight > 0 &&
                      value > roastInputWeight * 1.25
                    ) {
                      return t('inventory.createItem.errors.outputExceedsInput');
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <WeightInput
                    value={value}
                    onChange={onChange}
                    unit={unit}
                    disabled={isUpdateMode || readOnly}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${isUpdateMode || readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={t('inventory.createItem.quantityPlaceholder')}
                    data-test-id="inventory-create-quantity"
                  />
                )}
              />
            </div>
            {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('inventory.createItem.workOrderIdOptional')}</label>
            <input
              data-test-id="inventory-create-work-order-id"
              type="text"
              {...register('workOrderId')}
              readOnly={!!initialData?.workOrderId || readOnly}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${initialData?.workOrderId || readOnly ? 'bg-gray-100' : ''}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('inventory.createItem.expiryDateOptional')}</label>
            <input
              data-test-id="inventory-create-expiry-date"
              type="date"
              {...register('expiryDate')}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              disabled={readOnly}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            data-test-id="inventory-create-cancel"
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
          </button>
          {!readOnly && (
            <button
              data-test-id="inventory-create-submit"
              data-test-mode={mode ?? (initialData ? 'update' : 'create')}
              type="submit"
              disabled={isBusy || isOutputInvalid}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isBusy
                ? t('forms.actions.saving')
                : mode
                ? mode === 'create'
                  ? t('inventory.createItem.create')
                  : t('inventory.createItem.update')
                : initialData
                ? t('inventory.createItem.update')
                : t('inventory.createItem.create')}
            </button>
          )}
        </div>
      </form>

      <Modal
        isOpen={isShrinkageWarningOpen}
        onClose={() => setIsShrinkageWarningOpen(false)}
        title={t('inventory.createItem.shrinkageModal.title')}
        maxWidth="md"
      >
        <div className="space-y-4">
          {/* Box Warning Kuning */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700 font-bold">
                  {t('inventory.createItem.shrinkageModal.warningPrefix')} {currentShrinkage.toFixed(2)}%
                </p>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{t('inventory.createItem.shrinkageModal.comparisonDetails')}</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>{t('inventory.createItem.shrinkageModal.input')}: <b>{roastInputWeight?.toFixed(3)} kg</b></li>
                    <li>{t('inventory.createItem.shrinkageModal.output')}: <b>{pendingSubmitData?.quantity?.toFixed(3)} kg</b></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-700">
            {t('inventory.createItem.shrinkageModal.confirm')}
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsShrinkageWarningOpen(false);
                setPendingSubmitData(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              data-test-id="inventory-shrinkage-cancel"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (pendingSubmitData) {
                  void submitToParent(pendingSubmitData);
                }
                setIsShrinkageWarningOpen(false);
                setPendingSubmitData(null);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              data-test-id="inventory-shrinkage-proceed"
            >
              {t('purchaseOrder.form.modals.proceed')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
